import * as React from 'react';
import { useEffect, useRef } from 'react';

import { use$ } from '@legendapp/state/react';
import styled, { css } from 'styled-components';

import { DraggingEvent, useDragEvent } from '../../hooks/use-drag-event';

import { ContentId, ProductView, TrackingEventName, ZoomActionTypes } from '../../store/stores/tracking/types';

import {
	degreeImageStore,
	degreeImageStoreActions,
	dragOverlayStoreActions,
	interiorStore,
	zoomStore,
	zoomStoreActions
} from '../../store';
import { trackingStore } from '../../store/stores/tracking-store';
import DegreeImageComponent from './degree-image-component';

const DegreeImageContainer = () => {
	const elementRef = useRef<HTMLDivElement>(null);
	const didDragRef = useRef(false);
	const currentDegree = use$(degreeImageStore.currentDegree) || 0;
	const isZoomed = use$(zoomStore.isZoomed);

	function moveBy(steps: number) {
		dragOverlayStoreActions.setIsDragOverlayVisible(false);

		let newDegree = (currentDegree + steps * 10) % 360;
		if (newDegree < 0) newDegree += 360;
		degreeImageStoreActions.setCurrentDegree(newDegree);
	}

	const dragState = useDragEvent(elementRef, {
		onDragging: (e: DraggingEvent) => {
			moveBy(e.direction === 'left' ? 1 : -1);
		},
		onDragStart: () => {
			didDragRef.current = true;
			dragOverlayStoreActions.setIsDragOverlayVisible(false);
			trackingStore.track(TrackingEventName.VISUALIZER_ROTATE_CLICK, {
				contentId: ContentId.IMAGE_BASED_VIEW,
				sectionName: ProductView.EXTERNAL_VIEW
			});
		}
	});

	function handleZoomClick() {
		if (didDragRef.current) return;

		dragOverlayStoreActions.setIsDragOverlayVisible(false);

		trackingStore.track(TrackingEventName.VISUALIZER_VEHICLE_ZOOM, {
			contentId: ZoomActionTypes.UI_INTERACTION
		});
		zoomStoreActions.toggleOneStepZoom();
	}

	// TODO: let's try and remove the useEffects
	useEffect(() => {
		const handleKeyboard = (e: KeyboardEvent) => {
			if (interiorStore.isInteriorLoaded.get()) return;
			const target = e.target as HTMLElement | null;
			if (target && ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)) return;

			if (e.key === 'ArrowRight') moveBy(-1);
			if (e.key === 'ArrowLeft') moveBy(1);
		};

		window.addEventListener('keydown', handleKeyboard);
		return () => window.removeEventListener('keydown', handleKeyboard);
	}, [currentDegree]);

	return (
		<DegreeImageWrapper
			ref={elementRef}
			isPanning={use$(dragState.isDragging)}
			$isZoomed={isZoomed}
			onPointerDown={() => {
				didDragRef.current = false;
			}}
			onClick={handleZoomClick}
		>
			<DegreeImageComponent />
		</DegreeImageWrapper>
	);
};

const DegreeImageWrapper = styled.div<{ isPanning?: boolean; $isZoomed?: boolean }>`
	overflow: hidden;
	grid-column: 1 / -1;
	grid-row: 1 / -1;
	cursor: ${({ isPanning, $isZoomed }) => {
		if (isPanning) return 'grabbing';
		return $isZoomed ? 'zoom-out' : 'zoom-in';
	}};

	// This solves dragging up and down while dragging left/right
	touch-action: none;

	position: relative;
	-webkit-tap-highlight-color: transparent;

	${({ $isZoomed }) =>
		!$isZoomed &&
		css`
			@media (hover: hover) {
				&:hover img {
					transform: scale(1.03);
				}
			}
		`}
`;

export default DegreeImageContainer;
